import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API } from "@/lib/api";
import { listCases, deleteCase as deleteCaseOffline } from "@/lib/offlineApi";
import MobileCaseCard from "@/components/MobileCaseCard";
import OmfsCategoryChip from "@/components/OmfsCategoryChip";
import { OMFS_CATEGORIES } from "@/lib/omfsCategories";
import {
  Search, Filter, Trash2, Eye, Download, PlusCircle, X, RefreshCw, Copy, Pencil, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = ["", "Primary Surgeon", "First Assist", "Second Assist", "Teaching Assist", "Observer"];

function CaseDetailPanel({ c, onClose, onDelete, onDuplicate, onEdit }) {
  if (!c) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="case-detail-panel">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-xl bg-neu-bg shadow-neu overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-neu-bg/95 backdrop-blur px-6 py-4 border-b border-neu-muted/20 flex items-center justify-between">
          <div>
            <div className="text-xs subtle uppercase tracking-wider">{c.date_of_procedure}</div>
            <div className="heading text-xl text-[#3F4E5B]">{c.procedure_name}</div>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-neu-surface"
            onClick={onClose}
            data-testid="case-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs subtle uppercase">Patient</div>
              <div className="font-medium">{c.patient_name || c.patient_identifier || "—"}</div>
              <div className="text-xs subtle">
                {c.patient_dob ? `DOB ${c.patient_dob} · ` : ""}
                {c.patient_age ? `${c.patient_age}y` : "—"} · {c.patient_sex || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs subtle uppercase">Category</div>
              <div className="font-medium">{c.procedure_category || "—"}</div>
              {c.omfs_procedure_category && (
                <div className="mt-2" data-testid="case-detail-omfs-category">
                  <OmfsCategoryChip category={c.omfs_procedure_category} />
                </div>
              )}
            </div>
            <div>
              <div className="text-xs subtle uppercase">Role</div>
              <div className="font-medium">{c.role_in_case}</div>
            </div>
            <div>
              <div className="text-xs subtle uppercase">Approach</div>
              <div className="font-medium">{c.approach || "—"}</div>
            </div>
            <div>
              <div className="text-xs subtle uppercase">Attending</div>
              <div className="font-medium">{c.attending_physician}</div>
              {c.second_surgeon && (
                <div className="text-xs subtle mt-1">+ {c.second_surgeon}</div>
              )}
            </div>
            <div>
              <div className="text-xs subtle uppercase">Institution</div>
              <div className="font-medium">{c.institution}</div>
            </div>
          </div>

          <div>
            <div className="text-xs subtle uppercase mb-2">CPT codes</div>
            <div className="flex flex-wrap gap-2">
              {(c.cpt_codes || []).length === 0 && <span className="text-xs subtle">None</span>}
              {(c.cpt_codes || []).map((x) => (
                <span key={x.code} className="chip chip-green" title={x.description}>
                  <span className="font-semibold">{x.code}</span>
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs subtle uppercase mb-2">ICD-10 codes</div>
            <div className="flex flex-wrap gap-2">
              {(c.icd10_codes || []).length === 0 && <span className="text-xs subtle">None</span>}
              {(c.icd10_codes || []).map((x) => (
                <span key={x.code} className="chip" title={x.description}>
                  <span className="font-semibold">{x.code}</span>
                </span>
              ))}
            </div>
          </div>

          {c.notes && (
            <div>
              <div className="text-xs subtle uppercase mb-2">Notes</div>
              <div className="text-sm whitespace-pre-line">{c.notes}</div>
            </div>
          )}
          {c.teaching_points && (
            <div>
              <div className="text-xs subtle uppercase mb-2">Teaching points</div>
              <div className="text-sm whitespace-pre-line">{c.teaching_points}</div>
            </div>
          )}

          <div className="pt-4 border-t border-neu-muted/20 flex justify-between items-center gap-3 flex-wrap">
            <div className="flex gap-4 flex-wrap">
              <button
                onClick={() => onEdit(c.id)}
                className="text-sm text-neu-accent inline-flex items-center gap-2 hover:underline"
                data-testid="case-detail-edit"
              >
                <Pencil className="w-4 h-4" /> Edit this case
              </button>
              <button
                onClick={() => onDuplicate(c.id)}
                className="text-sm text-neu-text inline-flex items-center gap-2 hover:text-neu-accent"
                data-testid="case-detail-duplicate"
              >
                <Copy className="w-4 h-4" /> Duplicate
              </button>
            </div>
            <button
              onClick={() => onDelete(c.id)}
              className="text-sm text-red-600 inline-flex items-center gap-2 hover:underline"
              data-testid="case-detail-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete case
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function CaseList() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [attendings, setAttendings] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    search: "", role: "", attending: "", category: "", omfs_category: "", month: "",
  });
  const [backfilling, setBackfilling] = useState(false);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/cases/backfill-omfs-category");
      const tagged = data?.updated || 0;
      // After tagging, standardise Orthognathic case names from linked CPTs.
      let normalized = 0;
      try {
        const { data: norm } = await api.post(
          "/cases/normalize-orthognathic-descriptions",
        );
        normalized = norm?.updated || 0;
      } catch (_) {
        /* non-fatal */
      }
      if (tagged === 0 && normalized === 0) {
        toast.info("Nothing to organise — cases already up to date");
      } else {
        const bits = [];
        if (tagged) {
          const byCat = Object.entries(data.by_category || {})
            .map(([k, v]) => `${v} ${k}`).join(", ");
          bits.push(`Tagged ${tagged} case${tagged === 1 ? "" : "s"} (${byCat})`);
        }
        if (normalized) {
          bits.push(`Standardised ${normalized} orthognathic name${normalized === 1 ? "" : "s"}`);
        }
        toast.success(bits.join(" · "));
      }
      load();
    } catch (err) {
      toast.error("Organise failed");
    } finally {
      setBackfilling(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.attending) params.attending = filters.attending;
      if (filters.category) params.category = filters.category;
      if (filters.omfs_category) params.omfs_category = filters.omfs_category;
      if (filters.month) params.month = filters.month;
      const data = await listCases(params);
      setCases(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/staff").then(({ data }) => setAttendings((data || []).map((s) => s.name)));
    api.get("/procedure-categories").then(({ data }) => setCategories(Object.keys(data || {})));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filters.search, filters.role, filters.attending, filters.category, filters.omfs_category, filters.month]);

  const deleteCase = async (id) => {
    try {
      await deleteCaseOffline(id);
      toast.success("Case deleted");
      setSelected(null);
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Could not delete case");
    }
  };

  const exportFile = async (kind) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/cases/export/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fellowlog_cases.${kind === "excel" ? "xlsx" : "pdf"}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${kind.toUpperCase()} downloaded`);
  };

  const clearFilters = () =>
    setFilters({ search: "", role: "", attending: "", category: "", omfs_category: "", month: "" });

  return (
    <Layout>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">Case log</h1>
          <p className="subtle mt-2">{cases.length} case{cases.length !== 1 ? "s" : ""} found</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/cases/new" className="btn-accent inline-flex items-center gap-2" data-testid="cases-add">
            <PlusCircle className="w-4 h-4" /> Log case
          </Link>
          <button
            onClick={runBackfill}
            disabled={backfilling}
            className="btn-neu inline-flex items-center gap-2"
            data-testid="cases-backfill-omfs"
            title="Auto-tag OMFS category on older cases and standardise orthognathic descriptions from linked CPT codes"
          >
            <Wand2 className={`w-4 h-4 ${backfilling ? "animate-spin" : ""}`} />
            {backfilling ? "Organising…" : "Organise legacy cases"}
          </button>
          <button onClick={() => exportFile("excel")} className="btn-neu inline-flex items-center gap-2" data-testid="export-excel">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => exportFile("pdf")} className="btn-neu inline-flex items-center gap-2" data-testid="export-pdf">
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="neu-card p-5 md:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-neu-accent" />
          <div className="heading text-sm uppercase tracking-wider text-neu-text">Filters</div>
          <button
            onClick={clearFilters}
            className="ml-auto text-xs subtle hover:text-neu-accent inline-flex items-center gap-1"
            data-testid="clear-filters"
          >
            <RefreshCw className="w-3 h-3" /> Clear
          </button>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neu-muted pointer-events-none" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => set("search", e.target.value)}
              placeholder="Search procedure, attending, institution…"
              className="neu-input pl-10"
              data-testid="filter-search"
            />
          </div>
          <select
            value={filters.role}
            onChange={(e) => set("role", e.target.value)}
            className="neu-input"
            data-testid="filter-role"
          >
            {ROLES.map((r) => <option key={r} value={r}>{r || "All roles"}</option>)}
          </select>
          <select
            value={filters.attending}
            onChange={(e) => set("attending", e.target.value)}
            className="neu-input"
            data-testid="filter-attending"
          >
            <option value="">All attendings</option>
            {attendings.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={filters.category}
            onChange={(e) => set("category", e.target.value)}
            className="neu-input"
            data-testid="filter-category"
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={filters.omfs_category}
            onChange={(e) => set("omfs_category", e.target.value)}
            className="neu-input"
            data-testid="filter-omfs-category"
          >
            <option value="">All OMFS categories</option>
            {OMFS_CATEGORIES.map((c) => (
              <option key={c} value={c} data-testid={`filter-omfs-category-opt-${c}`}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="month"
            value={filters.month}
            onChange={(e) => set("month", e.target.value)}
            className="neu-input"
            data-testid="filter-month"
          />
        </div>
      </div>

      {loading ? (
        <div className="neu-card p-8 text-center subtle">Loading cases…</div>
      ) : cases.length === 0 ? (
        <div className="neu-card p-12 text-center">
          <div className="text-lg heading text-[#3F4E5B]">No cases match your filters</div>
          <p className="subtle mt-2">Try clearing filters or logging your first case.</p>
          <Link to="/cases/new" className="btn-accent inline-flex mt-6">
            <PlusCircle className="w-4 h-4 mr-2" /> Log a case
          </Link>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block neu-card p-4 overflow-x-auto" data-testid="cases-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left subtle text-xs uppercase tracking-wider border-b border-neu-muted/20">
                  <th className="py-3 px-3">Date</th>
                  <th className="py-3 px-3">Procedure</th>
                  <th className="py-3 px-3">CPT</th>
                  <th className="py-3 px-3">Attending</th>
                  <th className="py-3 px-3">Role</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-neu-muted/10 last:border-b-0 hover:bg-neu-bg/60 cursor-pointer transition-colors"
                    onClick={() => setSelected(c)}
                    data-testid={`case-row-${c.id}`}
                  >
                    <td className="py-3 px-3 whitespace-nowrap">{c.date_of_procedure}</td>
                    <td className="py-3 px-3">
                      <div className="font-medium text-neu-text line-clamp-1">{c.procedure_name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {c.procedure_category && (
                          <span className="text-xs subtle">{c.procedure_category}</span>
                        )}
                        <OmfsCategoryChip
                          category={c.omfs_procedure_category}
                          size="xs"
                          testId={`case-row-omfs-chip-${c.id}`}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs">
                      {(c.cpt_codes || []).slice(0, 2).map((x) => x.code).join(", ")}
                      {(c.cpt_codes || []).length > 2 && ` +${c.cpt_codes.length - 2}`}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">{c.attending_physician}</td>
                    <td className="py-3 px-3">
                      <span className={`chip ${c.role_in_case === "Primary Surgeon" ? "chip-green" : ""}`}>
                        {c.role_in_case}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelected(c); }}
                        className="text-neu-text hover:text-neu-accent p-2"
                        aria-label="View"
                        data-testid={`case-view-${c.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/cases/new?edit=${c.id}`); }}
                        className="text-neu-text hover:text-neu-accent p-2"
                        aria-label="Edit"
                        data-testid={`case-edit-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/cases/new?from=${c.id}`); }}
                        className="text-neu-text hover:text-neu-accent p-2"
                        aria-label="Duplicate"
                        data-testid={`case-duplicate-${c.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(c); }}
                        className="text-neu-text hover:text-red-600 p-2"
                        aria-label="Delete"
                        data-testid={`case-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden grid gap-4" data-testid="cases-mobile-list">
            {cases.map((c) => (
              <MobileCaseCard key={c.id} c={c} onClick={() => setSelected(c)} />
            ))}
          </div>
        </>
      )}

      <CaseDetailPanel
        c={selected}
        onClose={() => setSelected(null)}
        onDelete={(id) => setConfirmDelete(cases.find((x) => x.id === id))}
        onDuplicate={(id) => { setSelected(null); nav(`/cases/new?from=${id}`); }}
        onEdit={(id) => { setSelected(null); nav(`/cases/new?edit=${id}`); }}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-6" data-testid="confirm-delete-modal">
          <div className="neu-card p-6 max-w-sm w-full bg-[#E4EBF1]">
            <div className="heading text-lg text-[#3F4E5B]">Delete this case?</div>
            <p className="text-sm subtle mt-2">
              {confirmDelete.procedure_name} — {confirmDelete.date_of_procedure}. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirmDelete(null)} className="btn-neu" data-testid="confirm-delete-cancel">
                Cancel
              </button>
              <button
                onClick={() => deleteCase(confirmDelete.id)}
                className="btn-accent bg-red-600 shadow-red-600/40 hover:bg-red-700"
                style={{ backgroundColor: "#dc2626" }}
                data-testid="confirm-delete-confirm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
